import { BenchmarkSource, PropertyCategory } from '../models/ci-form.model';

export interface SelectOption<T = string> {
  value: T;
  label: string;
}

export type CategoryIcon = 'building' | 'home' | 'land' | 'layers';

export interface CategoryDefinition {
  value: PropertyCategory;
  label: string;
  /** Sub-label under the title, and the "CI Level" shown in the badge strip. */
  basis: string;
  icon: CategoryIcon;
  requiresTransactions: boolean;
}

export const PROPERTY_CATEGORIES: readonly CategoryDefinition[] = [
  {
    value: 'primary-project',
    label: 'Primary Project',
    basis: 'Project Level',
    icon: 'building',
    requiresTransactions: true,
  },
  {
    value: 'resale',
    label: 'Resale Property',
    basis: 'Individual Unit / Listing Level',
    icon: 'home',
    requiresTransactions: true,
  },
  {
    value: 'plot-land',
    label: 'Plot / Land',
    basis: 'Land Value',
    icon: 'land',
    requiresTransactions: false,
  },
  {
    value: 'independent-house',
    label: 'Independent House',
    basis: 'Land + Building',
    icon: 'layers',
    requiresTransactions: false,
  },
];

export function categoryDefinition(value: PropertyCategory): CategoryDefinition {
  return PROPERTY_CATEGORIES.find((c) => c.value === value) ?? PROPERTY_CATEGORIES[0];
}

/** Land and houses are valued directly, without comparable unit sales. */
export function isLandLed(category: PropertyCategory): boolean {
  return category === 'plot-land' || category === 'independent-house';
}

/* ── Benchmark source (step 2 — Benchmarking) ─────────────────────────────── */

export interface BenchmarkSourceDefinition {
  value: BenchmarkSource;
  label: string;
  description: string;
  recommended: boolean;
}

export const BENCHMARK_SOURCES: readonly BenchmarkSourceDefinition[] = [
  {
    value: 'transactions',
    label: 'Use Property Transactions',
    description: "Use this property's transaction history to build the benchmark.",
    recommended: true,
  },
  {
    value: 'similar-listings',
    label: 'Use Similar Listed Properties',
    description:
      'Use comparable listed properties from the same micro market. Benchmark will be the average of their CI Benchmarks.',
    recommended: false,
  },
  {
    value: 'anchor',
    label: 'Use Anchor Properties',
    description: 'Use non-listed / external properties as benchmark references.',
    recommended: false,
  },
];

export function benchmarkSourceDefinition(value: BenchmarkSource): BenchmarkSourceDefinition {
  return BENCHMARK_SOURCES.find((s) => s.value === value) ?? BENCHMARK_SOURCES[0];
}

/**
 * The blend behind the base benchmark when it is derived from this property's
 * own transaction history: mostly the recent trend, with a pull toward the
 * single latest print. Once the latest transaction is older than the recency
 * window, it stops pulling the average around and the benchmark rests on the
 * last-5 average alone.
 */
export const BENCHMARK_RECENCY_WINDOW_MONTHS = 12;
export const BENCHMARK_LAST5_WEIGHT = 0.65;
export const BENCHMARK_LATEST_TXN_WEIGHT = 0.35;
export const BENCHMARK_MAX_TRANSACTIONS = 5;

export const ZONING_OPTIONS: readonly SelectOption[] = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'mixed', label: 'Mixed use' },
  { value: 'agricultural', label: 'Agricultural' },
];

/* ── Plot / house options (labels only — the backend applies the maths) ────── */

export const ORIENTATION_OPTIONS: readonly SelectOption[] = [
  { value: 'north', label: 'North (0%)' },
  { value: 'north-east', label: 'North-East (0%)' },
  { value: 'east', label: 'East (0%)' },
  { value: 'south-east', label: 'South-East (−5%)' },
  { value: 'south', label: 'South (−5%)' },
  { value: 'south-west', label: 'South-West (−5%)' },
  { value: 'west', label: 'West (0%)' },
  { value: 'north-west', label: 'North-West (0%)' },
];

/**
 * Adjustment each orientation carries: South, South-East and South-West −5%
 * (applied once), every other facing 0%. Also used to reopen records that
 * stored only the fraction — the first matching key wins.
 */
export const ORIENTATION_ADJUSTMENTS: Record<string, number> = {
  east: 0,
  south: -0.05,
  north: 0,
  'north-east': 0,
  'south-east': -0.05,
  'south-west': -0.05,
  west: 0,
  'north-west': 0,
};

export const ROAD_OPTIONS: readonly { label: string; value: number }[] = [
  { label: 'Below 40 ft (0%)', value: 0 },
  { label: '40 – 59 ft (+10%)', value: 0.1 },
  { label: '60 ft and above (+15%)', value: 0.15 },
];

/** Road Width band for a width in ft: under 40 → 0, 40 to under 60 → +10%, 60 and above → +15%. */
export function roadAdjustmentFor(widthFt: number): number {
  return widthFt >= 60 ? 0.15 : widthFt >= 40 ? 0.1 : 0;
}

export const PREMIUM_OPTIONS: readonly { label: string; value: number }[] = [
  { label: 'No premium (0%)', value: 0 },
  { label: '+5% premium', value: 0.05 },
  { label: '+10% premium', value: 0.1 },
  { label: '+15% premium', value: 0.15 },
];

export const CONSTRUCTION_CLASS_OPTIONS: readonly SelectOption[] = [
  { value: 'basic', label: 'Basic' },
  { value: 'premium', label: 'Premium' },
];

/**
 * The 13–15 year band is the only one where the Supply Team picks the rate.
 * Mirrors the backend rate table, which validates the chosen rate.
 */
export const RANGED_BUILDING_BAND = {
  minAgeYears: 12,
  maxAgeYears: 15,
  rates: { basic: { min: 250, max: 500 }, premium: { min: 500, max: 750 } },
} as const;

export function needsBuildingRate(ageYears: number | null | undefined): boolean {
  return ageYears != null && ageYears > RANGED_BUILDING_BAND.minAgeYears && ageYears <= RANGED_BUILDING_BAND.maxAgeYears;
}

/** With the Supply Override on, a ₹ total and a reason are both required. */
export function isSupplyOverrideComplete(land: {
  supplyOverride: boolean;
  supplyOverrideValue: number | null;
  supplyOverrideReason: string | null;
}): boolean {
  if (!land.supplyOverride) return true;
  return Number(land.supplyOverrideValue) > 0 && !!land.supplyOverrideReason?.trim();
}
